/**
 * DNACore V4.9 — VariableBox (TipTap Custom Node)
 * Caixa de texto editável, draggable, resizable, com z-index/rotação/wrap
 * (mesmo padrão do ResizableImage). Suporta variáveis {{aluno_nome}} etc. com
 * auto-fill via window event 'ae-var-resolve'. Persiste como <div> com data-attrs
 * + inline styles para sobreviver em HTML/PDF/DOCX.
 */
import React, { useRef, useState, useEffect } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import {
  Bold, Italic, Underline, Move, Square, Trash2,
  ArrowUpToLine, ArrowDownToLine, Copy as CopyI, RotateCw,
} from 'lucide-react';

const DEFAULTS = {
  width: 180,
  height: 'auto',
  rotation: 0,
  floating: true,
  x: 0,
  y: 0,
  zIndex: 5,
  bold: false,
  italic: false,
  underline: false,
  align: 'left',
  fontSize: 12,
};

// ─── React Component (NodeView) ───
const VarBoxView = ({ node, updateAttributes, deleteNode, selected, editor, getPos }) => {
  const attrs = node.attrs;
  const ref = useRef(null);
  const [drag, setDrag] = useState(null);
  const [resize, setResize] = useState(null);
  const [rot, setRot] = useState(null);
  const [resolvedText, setResolvedText] = useState(attrs.text || '');

  const isFloat = !!attrs.floating;
  const rotation = Number(attrs.rotation) || 0;
  const width = Number(attrs.width) || DEFAULTS.width;
  const height = attrs.height === 'auto' ? 'auto' : Number(attrs.height);
  const zIndex = Number(attrs.zIndex) || DEFAULTS.zIndex;
  const fontSize = Number(attrs.fontSize) || DEFAULTS.fontSize;

  // V4.9: auto-fill via global event de meta
  useEffect(() => {
    const onResolve = (e) => {
      const vars = e.detail || {};
      const v = attrs.varName;
      if (v && vars[v]) setResolvedText(vars[v]);
    };
    window.addEventListener('ae-var-resolve', onResolve);
    // request initial values
    window.dispatchEvent(new CustomEvent('ae-var-request'));
    return () => window.removeEventListener('ae-var-resolve', onResolve);
  }, [attrs.varName]);

  // Drag
  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      updateAttributes({ x: drag.origX + dx, y: drag.origY + dy });
    };
    const onUp = () => setDrag(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [drag, updateAttributes]);

  // Resize
  useEffect(() => {
    if (!resize) return;
    const onMove = (e) => {
      const dx = e.clientX - resize.startX;
      updateAttributes({ width: Math.max(40, resize.origW + dx) });
    };
    const onUp = () => setResize(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resize, updateAttributes]);

  // Rotate
  useEffect(() => {
    if (!rot) return;
    const onMove = (e) => {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const ang = Math.atan2(e.clientY - cy, e.clientX - cx) * (180 / Math.PI) + 90;
      updateAttributes({ rotation: Math.round(ang) });
    };
    const onUp = () => setRot(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [rot, updateAttributes]);

  const startDrag = (e) => {
    if (!isFloat) return;
    if (e.target.tagName === 'SPAN' && e.target.contentEditable === 'true') return;
    e.preventDefault();
    setDrag({
      startX: e.clientX, startY: e.clientY,
      origX: Number(attrs.x) || 0, origY: Number(attrs.y) || 0,
    });
  };

  const tbBtn = (h) => ({
    onMouseDown: (e) => { e.preventDefault(); e.stopPropagation(); },
    onClick: (e) => { e.preventDefault(); e.stopPropagation(); h(); },
  });

  const toggleFloat = () => updateAttributes({ floating: !isFloat, x: 0, y: 0 });
  const toFront = () => updateAttributes({ zIndex: zIndex + 1 });
  const toBack = () => updateAttributes({ zIndex: Math.max(-5, zIndex - 1) });
  const toggleBold = () => updateAttributes({ bold: !attrs.bold });
  const toggleItalic = () => updateAttributes({ italic: !attrs.italic });
  const toggleUnderline = () => updateAttributes({ underline: !attrs.underline });
  const cycleAlign = () => {
    const order = ['left', 'center', 'right'];
    const idx = order.indexOf(attrs.align || 'left');
    updateAttributes({ align: order[(idx + 1) % order.length] });
  };
  const duplicate = () => {
    if (typeof getPos !== 'function' || !editor) return;
    const pos = getPos();
    editor.chain().focus().insertContentAt(pos + node.nodeSize, {
      type: 'variableBox',
      attrs: { ...attrs, x: (Number(attrs.x) || 0) + 20, y: (Number(attrs.y) || 0) + 20 },
    }).run();
  };

  const wrapStyle = isFloat ? {
    position: 'absolute',
    left: `${attrs.x || 0}px`,
    top: `${attrs.y || 0}px`,
    zIndex,
  } : { display: 'inline-block', zIndex };

  const displayText = attrs.varName ? (resolvedText || `{{${attrs.varName}}}`) : (attrs.text || 'Texto');

  return (
    <NodeViewWrapper
      as="span"
      className={`ae-varbox ${selected ? 'is-selected' : ''} ${isFloat ? 'is-float' : ''}`}
      style={wrapStyle}
      data-drag-handle
    >
      <span
        ref={ref}
        className="ae-varbox-box"
        style={{
          display: 'inline-block',
          width: `${width}px`,
          minHeight: 22,
          height: typeof height === 'number' ? `${height}px` : 'auto',
          transform: rotation ? `rotate(${rotation}deg)` : 'none',
          cursor: isFloat ? 'move' : 'default',
          padding: '3px 8px',
          border: selected ? '2px solid #7B3F98' : '1px dashed #cbd5e1',
          borderRadius: 4,
          background: 'rgba(255,255,255,0.92)',
          fontSize: `${fontSize}px`,
          fontWeight: attrs.bold ? 700 : 400,
          fontStyle: attrs.italic ? 'italic' : 'normal',
          textDecoration: attrs.underline ? 'underline' : 'none',
          textAlign: attrs.align || 'left',
          color: '#0f172a',
          lineHeight: 1.3,
        }}
        onMouseDown={startDrag}
      >
        <span
          contentEditable={!attrs.varName /* var bound auto-resolves; click to edit/replace */ ? true : true}
          suppressContentEditableWarning
          onBlur={(e) => {
            const t = e.currentTarget.textContent;
            // Se editou o conteúdo manualmente, salva como text custom (rompe o binding com varName)
            updateAttributes({ text: t });
          }}
          style={{ outline: 'none', display: 'block', minHeight: 16 }}
        >{displayText}</span>

        {selected && (
          <>
            {/* Resize handle (canto direito-inferior) */}
            <span className="ae-varbox-resize" onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setResize({ startX: e.clientX, origW: width }); }} title="Redimensionar" />
            {/* Rotate handle (topo) */}
            <span className="ae-varbox-rotate" onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); setRot({}); }} title="Rotacionar"><RotateCw size={9} /></span>
            {/* Toolbar contextual */}
            <span className="ae-varbox-toolbar" contentEditable={false} onMouseDown={(e) => e.stopPropagation()}>
              <button {...tbBtn(toggleBold)} className={attrs.bold ? 'on' : ''} title="Negrito" data-testid="vb-bold"><Bold size={11} /></button>
              <button {...tbBtn(toggleItalic)} className={attrs.italic ? 'on' : ''} title="Itálico" data-testid="vb-italic"><Italic size={11} /></button>
              <button {...tbBtn(toggleUnderline)} className={attrs.underline ? 'on' : ''} title="Sublinhado" data-testid="vb-underline"><Underline size={11} /></button>
              <button {...tbBtn(() => updateAttributes({ fontSize: Math.max(8, fontSize - 1) }))} title="A−" data-testid="vb-fontdec">A−</button>
              <button {...tbBtn(() => updateAttributes({ fontSize: Math.min(48, fontSize + 1) }))} title="A+" data-testid="vb-fontinc">A+</button>
              <button {...tbBtn(cycleAlign)} title={`Align: ${attrs.align}`} data-testid="vb-align">⇆</button>
              <span className="vb-sep" />
              <button {...tbBtn(toggleFloat)} title={isFloat ? 'Inline' : 'Flutuante'} data-testid="vb-float">{isFloat ? <Square size={11} /> : <Move size={11} />}</button>
              <button {...tbBtn(toFront)} title="Frente" data-testid="vb-front"><ArrowUpToLine size={11} /></button>
              <button {...tbBtn(toBack)} title="Trás" data-testid="vb-back"><ArrowDownToLine size={11} /></button>
              <button {...tbBtn(duplicate)} title="Duplicar" data-testid="vb-duplicate"><CopyI size={11} /></button>
              <button {...tbBtn(() => deleteNode())} title="Excluir" data-testid="vb-delete"><Trash2 size={11} /></button>
            </span>
          </>
        )}
      </span>
    </NodeViewWrapper>
  );
};

// ─── TipTap Node Extension ───
export const VariableBox = Node.create({
  name: 'variableBox',
  group: 'inline',
  inline: true,
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      varName: { default: null, parseHTML: el => el.getAttribute('data-var') || null },
      text:    { default: '',   parseHTML: el => el.getAttribute('data-text') || el.textContent || '' },
      width:   { default: DEFAULTS.width,    parseHTML: el => parseInt(el.getAttribute('data-w') || DEFAULTS.width, 10) || DEFAULTS.width },
      height:  { default: DEFAULTS.height,   parseHTML: el => { const h = el.getAttribute('data-h'); return (!h || h === 'auto') ? 'auto' : parseInt(h, 10) || 'auto'; } },
      rotation:{ default: 0,                 parseHTML: el => Number(el.getAttribute('data-rot') || 0) || 0 },
      floating:{ default: DEFAULTS.floating, parseHTML: el => el.getAttribute('data-float') === '1' },
      x:       { default: 0,                 parseHTML: el => Number(el.getAttribute('data-x') || 0) || 0 },
      y:       { default: 0,                 parseHTML: el => Number(el.getAttribute('data-y') || 0) || 0 },
      zIndex:  { default: DEFAULTS.zIndex,   parseHTML: el => Number(el.getAttribute('data-z') || DEFAULTS.zIndex) || DEFAULTS.zIndex },
      bold:    { default: false, parseHTML: el => el.getAttribute('data-b') === '1' },
      italic:  { default: false, parseHTML: el => el.getAttribute('data-i') === '1' },
      underline:{ default: false, parseHTML: el => el.getAttribute('data-u') === '1' },
      align:   { default: 'left', parseHTML: el => el.getAttribute('data-align') || 'left' },
      fontSize:{ default: DEFAULTS.fontSize, parseHTML: el => parseInt(el.getAttribute('data-fs') || DEFAULTS.fontSize, 10) || DEFAULTS.fontSize },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-ae-varbox="1"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const a = HTMLAttributes;
    const isFloat = !!a.floating;
    const w = Number(a.width) || DEFAULTS.width;
    const h = (a.height === 'auto' || !a.height) ? 'auto' : `${Number(a.height)}px`;
    const rot = Number(a.rotation) || 0;
    const z = Number(a.zIndex) || DEFAULTS.zIndex;
    const fs = Number(a.fontSize) || DEFAULTS.fontSize;
    const parts = [
      `display:inline-block`,
      `width:${w}px`,
      h !== 'auto' ? `height:${h}` : '',
      rot ? `transform:rotate(${rot}deg)` : '',
      `z-index:${z}`,
      `padding:3px 8px`,
      `font-size:${fs}px`,
      a.bold ? 'font-weight:700' : '',
      a.italic ? 'font-style:italic' : '',
      a.underline ? 'text-decoration:underline' : '',
      `text-align:${a.align || 'left'}`,
      `border:1px solid transparent`,
    ];
    if (isFloat) {
      parts.push('position:absolute', `left:${Number(a.x) || 0}px`, `top:${Number(a.y) || 0}px`);
    }
    const style = parts.filter(Boolean).join(';');
    const text = a.text || (a.varName ? `{{${a.varName}}}` : '');
    return ['span', mergeAttributes({}, {
      'data-ae-varbox': '1',
      'data-var': a.varName || '',
      'data-text': text,
      'data-w': String(w),
      'data-h': h === 'auto' ? 'auto' : String(Number(a.height)),
      'data-rot': String(rot),
      'data-float': isFloat ? '1' : '0',
      'data-x': String(Number(a.x) || 0),
      'data-y': String(Number(a.y) || 0),
      'data-z': String(z),
      'data-b': a.bold ? '1' : '0',
      'data-i': a.italic ? '1' : '0',
      'data-u': a.underline ? '1' : '0',
      'data-align': a.align || 'left',
      'data-fs': String(fs),
      style,
    }), text];
  },

  addNodeView() {
    return ReactNodeViewRenderer(VarBoxView);
  },

  addCommands() {
    return {
      insertVariableBox: (opts) => ({ commands }) => commands.insertContent({ type: this.name, attrs: opts || {} }),
    };
  },
});

export default VariableBox;
